import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import DocumentUploadDropzone from '@components/documents/DocumentUploadDropzone';
import DocumentMetadataForm from '@components/documents/DocumentMetadataForm';
import UploadProgressTracker from '@components/documents/UploadProgressTracker';
import { documentService } from '../services/document.service';

interface UploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error' | 'duplicate';
  progress: number;
  error?: string;
  statusMessage?: string;
  isDuplicate?: boolean;
  duplicateDocumentId?: string;
  willCreateNewVersion?: boolean;
  documentId?: string;  // Store the uploaded document ID
  metadata?: {
    category: string;
    tags: string[];
    description: string;
    permissions: any;
    existingVersion?: number;
    existingTitle?: string;
    existingDescription?: string;
    existingFileSize?: number;
    existingUploadedAt?: string;
  };
}

interface UploadStep {
  id: number;
  label: string;
  completed: boolean;
}

const DocumentUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedDuplicateFile, setSelectedDuplicateFile] = useState<UploadFile | null>(null);

  const steps: UploadStep[] = [
    { id: 0, label: t('upload.steps.selectFiles'), completed: false },
    { id: 1, label: t('upload.steps.addMetadata'), completed: false },
    { id: 2, label: t('upload.steps.uploadProcess'), completed: false },
  ];

  const handleFilesSelected = useCallback((files: File[]) => {
    const newUploadFiles: UploadFile[] = files.map((file, index) => ({
      id: `upload_${Date.now()}_${index}`,
      file,
      status: 'pending',
      progress: 0,
    }));

    setUploadFiles(prev => [...prev, ...newUploadFiles]);

    // Check for duplicates (simulated)
    setTimeout(() => {
      checkForDuplicates(newUploadFiles);
    }, 500);
  }, []);

  const checkForDuplicates = async (files: UploadFile[]) => {
    // Check each file for duplicates
    const updatedFiles: UploadFile[] = [];

    for (const file of files) {
      try {
        // Check if file already exists
        const formData = new FormData();
        formData.append('fileName', file.file.name);

        // Use API_URL which already includes /api/v1, so don't add it again
        let apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        // Remove /api/v1 suffix if present to prevent URL duplication
        if (apiUrl.endsWith('/api/v1')) {
          apiUrl = apiUrl.replace(/\/api\/v1$/, '');
        }
        // Include auth token for authenticated endpoints
        const token = sessionStorage.getItem('access_token');
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${apiUrl}/api/v1/documents/check-duplicate`, {
          method: 'POST',
          headers,
          body: formData,
        });

        if (response.ok) {
          const result = await response.json();

          if (result.isDuplicate && result.existingDocument) {
            updatedFiles.push({
              ...file,
              status: 'duplicate' as const,
              isDuplicate: true,
              duplicateDocumentId: result.existingDocument.id,
              metadata: {
                ...file.metadata,
                existingVersion: result.existingDocument.version,
                existingTitle: result.existingDocument.title,
                existingDescription: result.existingDocument.description,
                existingFileSize: result.existingDocument.fileSize,
                existingUploadedAt: result.existingDocument.uploadedAt,
              } as any,
            });
          } else {
            updatedFiles.push(file);
          }
        } else {
          // If check fails, proceed without duplicate detection
          updatedFiles.push(file);
        }
      } catch (error) {
        console.error('Error checking for duplicates:', error);
        updatedFiles.push(file);
      }
    }

    setUploadFiles(prev =>
      prev.map(prevFile => {
        const updated = updatedFiles.find(f => f.id === prevFile.id);
        return updated || prevFile;
      })
    );

    // Open duplicate dialog if any duplicates found
    const duplicateFile = updatedFiles.find(f => f.isDuplicate);
    if (duplicateFile) {
      setSelectedDuplicateFile(duplicateFile);
      setDuplicateDialogOpen(true);
    }
  };

  const handleFileRemove = (fileId: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleDuplicateAction = async (action: 'replace' | 'keep' | 'cancel') => {
    if (!selectedDuplicateFile) return;

    const fileId = selectedDuplicateFile.id;
    const fileToRetry = selectedDuplicateFile;

    if (action === 'cancel') {
      handleFileRemove(fileId);
    } else if (action === 'keep') {
      // Keep as separate file - treat as new upload (skip duplicate check)
      // Note: Backend doesn't support force upload, so user needs to rename or this will fail again
      // For now, we'll just reset the status and let user try again
      setUploadFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'pending', progress: 0, isDuplicate: false, duplicateDocumentId: undefined }
            : f
        )
      );
    } else if (action === 'replace') {
      // Replace existing - will create new version using as_new_version flag
      const updatedFile: UploadFile = {
        ...fileToRetry,
        status: 'pending',
        progress: 0,
        isDuplicate: false,
        duplicateDocumentId: undefined,
        willCreateNewVersion: true,
        metadata: {
          ...fileToRetry.metadata,
          category: fileToRetry.metadata?.category || 'general',
          tags: fileToRetry.metadata?.tags || [],
          description: fileToRetry.metadata?.description || '',
          permissions: fileToRetry.metadata?.permissions || {},
          existingVersion: undefined,
          existingTitle: undefined,
          existingDescription: undefined,
          existingFileSize: undefined,
          existingUploadedAt: undefined,
        },
      };

      setUploadFiles(prev =>
        prev.map(f => f.id === fileId ? updatedFile : f)
      );

      // Retry the upload with as_new_version flag
      setTimeout(async () => {
        await uploadSingleFile(updatedFile, { asNewVersion: true });
      }, 100);
    }

    setDuplicateDialogOpen(false);
    setSelectedDuplicateFile(null);

    // Check if there are more unresolved duplicates
    setTimeout(() => {
      const remainingDuplicates = uploadFiles.filter(f =>
        f.status === 'duplicate' && f.id !== fileId
      );

      if (remainingDuplicates.length > 0) {
        setSelectedDuplicateFile(remainingDuplicates[0]);
        setDuplicateDialogOpen(true);
      }
    }, 200);
  };

  const handleMetadataSubmit = (metadata: any) => {
    // Apply metadata to all pending files
    setUploadFiles(prev =>
      prev.map(file => ({
        ...file,
        metadata,
      }))
    );

    setActiveStep(2);
    startUpload();
  };

  const startUpload = async () => {
    setIsUploading(true);

    for (const file of uploadFiles.filter(f => f.status === 'pending')) {
      await uploadSingleFile(file);
    }

    setIsUploading(false);
    setActiveStep(2);
  };

  const uploadSingleFile = async (uploadFile: UploadFile, options?: { asNewVersion?: boolean }): Promise<void> => {
    try {
      // Update status to uploading
      setUploadFiles(prev =>
        prev.map(f =>
          f.id === uploadFile.id
            ? { ...f, status: 'uploading' }
            : f
        )
      );

      // Prepare metadata
      const metadata = {
        title: uploadFile.file.name,
        description: uploadFile.metadata?.description || '',
        category: uploadFile.metadata?.category || 'General',
        tags: uploadFile.metadata?.tags || [],
      };

      // Upload file with progress tracking
      const response = await documentService.uploadDocument(
        uploadFile.file,
        metadata,
        (progress) => {
          setUploadFiles(prev =>
            prev.map(f =>
              f.id === uploadFile.id
                ? { ...f, progress: Math.min(progress, 90) }
                : f
            )
          );
        },
        { asNewVersion: options?.asNewVersion || uploadFile.willCreateNewVersion }
      );

      const backgroundProcessingMessage = t('upload.backgroundProcessingMessage');

      const markProcessingInBackground = () => {
        setUploadFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id
              ? {
                  ...f,
                  status: 'processing',
                  progress: 95,
                  documentId: response.id,
                  statusMessage: backgroundProcessingMessage,
                }
              : f
          )
        );
      };

      // Update status to processing
      markProcessingInBackground();

      // Check upload status periodically
      let processingComplete = false;
      let retryCount = 0;
      const maxRetries = 30; // Poll briefly; background processing can continue longer.

      while (!processingComplete && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second

        let status: Awaited<ReturnType<typeof documentService.getUploadStatus>>;
        try {
          status = await documentService.getUploadStatus(response.id);
        } catch (error) {
          console.error('Error checking upload status:', error);
          markProcessingInBackground();
          break;
        }

        if (status.status === 'completed') {
          processingComplete = true;
          setUploadFiles(prev =>
            prev.map(f =>
              f.id === uploadFile.id
                ? {
                    ...f,
                    status: 'completed',
                    progress: 100,
                    documentId: response.id,
                    statusMessage: undefined,
                  }
                : f
            )
          );
        } else if (status.status === 'failed') {
          throw new Error(status.message || 'Processing failed');
        } else {
          markProcessingInBackground();
        }

        retryCount++;
      }

      if (!processingComplete) {
        markProcessingInBackground();
      }

    } catch (error: any) {
      console.error('Upload failed:', error);

      // Check if this is a duplicate file error (handle both with and without existingDocument)
      if (error.isDuplicate) {
        const duplicateFile: UploadFile = {
          ...uploadFile,
          status: 'duplicate',
          progress: 100,
          error: error.message,
          isDuplicate: true,
          duplicateDocumentId: error.existingDocument?.id,
          metadata: {
            ...uploadFile.metadata,
            category: uploadFile.metadata?.category || 'general',
            tags: uploadFile.metadata?.tags || [],
            description: uploadFile.metadata?.description || '',
            permissions: uploadFile.metadata?.permissions || {},
            // Use optional chaining for existingDocument fields
            existingVersion: error.existingDocument?.currentVersion || 1,
            existingTitle: error.existingDocument?.title,
            existingDescription: error.existingDocument?.description,
            existingFileSize: error.existingDocument?.fileSize,
            existingUploadedAt: error.existingDocument?.uploadedAt,
          },
        };

        setUploadFiles(prev =>
          prev.map(f => f.id === uploadFile.id ? duplicateFile : f)
        );

        // Open the duplicate dialog to let user choose how to handle it
        setSelectedDuplicateFile(duplicateFile);
        setDuplicateDialogOpen(true);
      } else {
        setUploadFiles(prev =>
          prev.map(f =>
            f.id === uploadFile.id
              ? {
                  ...f,
                  status: 'error',
                  progress: 100,
                  error: error.message || 'Failed to upload document',
                }
              : f
          )
        );
      }
    }
  };

  const handleRetryUpload = (fileId: string) => {
    const file = uploadFiles.find(f => f.id === fileId);
    if (file) {
      setUploadFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'pending', progress: 0, error: undefined }
            : f
        )
      );
    }
  };

  const handleManualFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleFilesSelected(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBackClick = () => {
    navigate('/documents');
  };

  const handleViewFile = () => {
    // Navigate to the first successfully uploaded file
    const completedFile = uploadFiles.find(f => f.status === 'completed' && f.documentId);
    if (completedFile?.documentId) {
      navigate(`/documents/${completedFile.documentId}`);
    } else {
      // Fallback to documents list if no completed file found
      navigate('/documents');
    }
  };

  const handleNextStep = () => {
    if (activeStep === 0 && uploadFiles.length === 0) {
      return; // Can't proceed without files
    }
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const canProceedToNext = () => {
    switch (activeStep) {
      case 0:
        // Allow proceeding if we have files and no unresolved duplicates
        return uploadFiles.length > 0 && !uploadFiles.some(f => f.status === 'duplicate');
      case 1:
        return uploadFiles.every(f => f.metadata);
      case 2:
        return true;
      default:
        return false;
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('upload.selectDocuments')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('upload.selectDocumentsDesc')}
            </Typography>

            <DocumentUploadDropzone onFilesSelected={handleFilesSelected} />

            <Box textAlign="center" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={handleManualFileSelect}
              >
                {t('upload.browseFiles')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.txt,.zip"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />
            </Box>

            {uploadFiles.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('upload.selectedFiles')} ({uploadFiles.length})
                  </Typography>
                  <List dense>
                    {uploadFiles.map((uploadFile) => (
                      <ListItem key={uploadFile.id}>
                        <ListItemIcon>
                          {uploadFile.status === 'duplicate' ? (
                            <WarningIcon color="warning" />
                          ) : (
                            <AttachFileIcon />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={uploadFile.file.name}
                          secondary={`${(uploadFile.file.size / 1024 / 1024).toFixed(2)} MB`}
                        />
                        {uploadFile.status === 'duplicate' && (
                          <Chip label={t('upload.duplicate')} color="warning" size="small" sx={{ mr: 1 }} />
                        )}
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleFileRemove(uploadFile.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('upload.addMetadata')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('upload.addMetadataDesc')}
            </Typography>

            <DocumentMetadataForm
              onSubmit={handleMetadataSubmit}
              fileCount={uploadFiles.length}
            />
          </Box>
        );

      case 2: {
        const isUploadFinished = uploadFiles.every(f => ['completed', 'error'].includes(f.status));
        const hasUploadErrors = uploadFiles.some(f => f.status === 'error');
        const hasBackgroundProcessing = uploadFiles.some(
          f => f.status === 'processing' && Boolean(f.documentId)
        );

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('upload.uploadProgressTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('upload.uploadProgressDesc')}
            </Typography>

            <UploadProgressTracker
              uploadFiles={uploadFiles}
              onRetry={handleRetryUpload}
            />

            {hasBackgroundProcessing && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="subtitle2">
                  {t('upload.backgroundProcessingTitle')}
                </Typography>
                <Typography variant="body2">
                  {t('upload.backgroundProcessingDesc')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleViewFile}
                  sx={{ mt: 1 }}
                >
                  {t('upload.viewDocuments')}
                </Button>
              </Alert>
            )}

            {isUploadFinished && (
              <Alert severity={hasUploadErrors ? 'warning' : 'success'} sx={{ mt: 3 }}>
                <Typography variant="subtitle2">
                  {hasUploadErrors
                    ? t('upload.uploadFinishedWithErrors', '一部のファイル処理に失敗しました')
                    : t('upload.uploadComplete')}
                </Typography>
                <Typography variant="body2">
                  {t('upload.uploadCompleteDesc', {
                    completed: uploadFiles.filter(f => f.status === 'completed').length,
                    total: uploadFiles.length
                  })}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleViewFile}
                  sx={{ mt: 1 }}
                >
                  {t('upload.viewDocuments')}
                </Button>
              </Alert>
            )}
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleBackClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{t('upload.pageTitle')}</Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.id} completed={step.completed}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {getStepContent()}
      </Paper>

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between">
        <Button
          onClick={handlePrevStep}
          disabled={activeStep === 0}
          variant="outlined"
        >
          {t('upload.back')}
        </Button>

        {activeStep < steps.length - 1 && (
          <Button
            onClick={handleNextStep}
            disabled={!canProceedToNext() || isUploading}
            variant="contained"
          >
            {t('upload.next')}
          </Button>
        )}
      </Box>

      {/* Duplicate File Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('upload.duplicateDetected')}
          {selectedDuplicateFile && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'normal', mt: 1 }}>
              {t('upload.duplicateFileName', { name: selectedDuplicateFile.file.name })}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('upload.duplicatePrompt')}
          </Alert>
          {selectedDuplicateFile && selectedDuplicateFile.metadata && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('upload.uploadingFile')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                • {t('upload.fileName')}: {selectedDuplicateFile.file.name}<br />
                • {t('upload.fileSize')}: {(selectedDuplicateFile.file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                {t('upload.existingFileInfo')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {t('upload.title')}: {(selectedDuplicateFile.metadata as any).existingTitle || selectedDuplicateFile.file.name}<br />
                • {t('upload.currentVersion')}: Version {(selectedDuplicateFile.metadata as any).existingVersion || 1}<br />
                • {t('upload.fileSize')}: {((selectedDuplicateFile.metadata as any).existingFileSize ? ((selectedDuplicateFile.metadata as any).existingFileSize / 1024 / 1024).toFixed(2) : t('common.unknown'))} MB<br />
                • {t('upload.uploadedAt')}: {(selectedDuplicateFile.metadata as any).existingUploadedAt ? new Date((selectedDuplicateFile.metadata as any).existingUploadedAt).toLocaleString(i18n.language) : t('common.unknown')}
              </Typography>

              <Alert severity="info" sx={{ mt: 2 }}>
                {t('upload.newVersionInfo', {
                  version: ((selectedDuplicateFile.metadata as any).existingVersion || 1) + 1,
                })}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDuplicateAction('cancel')} color="error">
            {t('upload.cancelUpload')}
          </Button>
          <Button onClick={() => handleDuplicateAction('keep')} variant="outlined">
            {t('upload.saveAsSeparate')}
          </Button>
          <Button
            onClick={() => handleDuplicateAction('replace')}
            variant="contained"
            color="primary"
          >
            {t('upload.saveAsNewVersion')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentUploadPage;
